import Canvas from '../Canvas/Canvas'

interface IProps {
  readonly animationTime: number // a mutató forgatásnál a transition time
  readonly endingAngle:   number // az óramutató befejező állása
  readonly initAngle:     number // az óramutató kezdő állása
  readonly height:        number
  readonly radius:        number
  readonly color:         string
  readonly width:         number
  readonly left:          number
  readonly top:           number
  readonly onEnd?:        TVoidCallback
  readonly elpasedAngle?: number
}

/**
 * Eltelt időt jelző komponens (kör).
 * @param props - Létrehozni kívánt Canvas paraméterei {@link IProps}.
 */
export default function ElapsedTime (props: IProps): HTMLCanvasElement | null {
  const x = props.width  / 2 // a canvas teljes magasságának fele, így tudjuk vertikálisan középre pozícionálni a rajzot
  const y = props.height / 2 // a canvas teljes szélességének fele, így tudjuk horizontálisan középre pozícionálni a rajzot

  // Ennyit kell megtenni
  const maxDiffAngle = (
    props.endingAngle > props.initAngle // ez a jó eset: nagyobb a vége, mint a kezdés
      ? props.endingAngle - props.initAngle
    // Ekkor kisebb a kezdő, mint a vége
      : (
        (360 - props.initAngle) + // A teljes kör és a kezdő közt a különbség
      props.endingAngle         // PLUSZ a vége, hogy kijöjjön az egész :D
      )
  )

  // Ebből számoljuk vissza a még hátralevő fokot
  let actDiffAngle = maxDiffAngle

  const rawStartAngle = props.initAngle - (props.elpasedAngle ?? 0)

  // Kezdőérték
  const startAngle = (rawStartAngle * (Math.PI / 180)) - (Math.PI / 2)

  // Aktuális érték
  let actAngle = props.initAngle

  let actTime = 0

  /**
   * Frissítés.
   * @param deltaTime - Frissítések gyakorisága.
   */
  function update (deltaTime: number): void {
    if (actDiffAngle < 0) {
      return
    }

    actTime = actTime + deltaTime // Itt járunk az eltelt idővel.

    const percent   = actTime / (props.animationTime / 100) // hány százaléknál járunk
    const angleStep = (maxDiffAngle / 100) * percent        // ennyivel módosult az előző updatehez képest

    actDiffAngle = maxDiffAngle - angleStep
    actAngle = props.initAngle + angleStep

    if (percent >= 100 && props.onEnd) {
      props.onEnd()
    }
  }

  /**
   * Kirajzolásért felelős függvény.
   * @param ctx - Biztosítja a 2D renderelési kontextust egy <canvas> elem rajzfelületéhez.
   */
  function render (ctx: CanvasRenderingContext2D): void {
    if (actDiffAngle < 0) {
      return
    }

    const temporaryAngle = -(Math.PI / 2) + (actAngle * (Math.PI / 180))

    ctx.clearRect(0, 0, props.width, props.height)
    ctx.beginPath()
    ctx.moveTo(x, y)
    ctx.arc(x, y, props.radius, startAngle, temporaryAngle)
    ctx.fillStyle = props.color
    ctx.fill()
  }

  const canvas: HTMLCanvasElement = (
    <Canvas
      width={ props.width }
      height={ props.height }
      hasAlpha
      left={ props.left }
      render={ render }
      top={ props.top }
      update={ update }
      z={ 0 }
    />
  )

  return canvas
}
